import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { assertArray } from '@app/core/utilities/array.utilities';

import { environment } from '@env/environment';

import { valueToCamelCase } from '@app/core/utilities/string.utilities';
import { API_MAX_LIMIT } from '../models/api.models';
import {
  BUDGET_AMOUNT,
  CLICK_THROUGH_RATE,
  CLICK_THROUGH_RATE_CLICKS,
  CLICKS,
  CONVERSION_RATE,
  CONVERSION_RATE_ADJUSTED,
  CONVERSION_RATE_CLICKS,
  CONVERSIONS,
  CONVERSIONS_ADJUSTED,
  CONVERSIONS_CLICKS,
  COST,
  COST_DAY,
  COST_FOR_CLICK,
  COST_FOR_PROFIT,
  COST_FOR_REVENUE,
  COST_FOR_VCPM,
  COST_PER_CLICK,
  COST_PER_CONVERSION,
  COST_PER_CONVERSION_ADJUSTED,
  COST_PER_CONVERSION_CLICKS,
  COST_PER_MILLE,
  COST_REVENUE_RATIO,
  COST_REVENUE_RATIO_ADJUSTED,
  COST_REVENUE_RATIO_CLICKS,
  COST_REVENUE_TOTAL_RATIO,
  COST_REVENUE_TOTAL_RATIO_ROLLING_AVG,
  currencyGoals,
  CustomerPerfEndpointAllColumns,
  IMPRESSIONS,
  IMPRESSIONS_TOTAL,
  PRICE,
  PROFIT_GROSS,
  PROFIT_GROSS_ADJUSTED,
  PROFIT_NET,
  PROFIT_NET_ADJUSTED,
  RETURN_ON_INVESTMENT,
  RETURN_ON_INVESTMENT_ADJUSTED,
  REVENUE,
  REVENUE_ADJUSTED,
  REVENUE_CLICKS,
  REVENUE_ORGANIC,
  REVENUE_ORGANIC_ADJUSTED,
  REVENUE_ORGANIC_ROLLING_AVG,
  REVENUE_PER_CONVERSION,
  REVENUE_TOTAL,
  REVENUE_TOTAL_ROLLING_AVG,
  ROAS,
  ROAS_ADJUSTED,
  ROAS_CLICKS,
  VIEW_IMPRESSIONS,
} from '../models/metrics.constants';
import { LoggingService } from '../services/error-logging.service';
import {
  averageByMetric,
  convertMicrostoNumbers,
  getACoS,
  getClickThroughRate,
  getConversionRate,
  getCostPerConversion,
  getEffectiveCPC,
  getEffectiveCPM,
  getReturnOnAdSpend,
  getReturnOnInvestment,
  summationByMetric,
} from '../services/metrics-calculations.service';
import { UtilitiesService } from '../services/utilities.service';
import { GetDaysCountFromRefiners } from '../utilities/date.utilities';
import { deepClone } from '../utilities/object.utilities';

@Injectable()
export class KeyFiguresAPI {
  constructor(
    private http: HttpClient,
    private logger: LoggingService,
    private utilitiesService: UtilitiesService,
  ) {}

  public getFiguresSummary(uri: string, refiners: any, groupByDate: boolean, lessThan7: boolean) {
    const dateDiff = GetDaysCountFromRefiners(refiners);
    if (groupByDate) {
      refiners.push(['limit', API_MAX_LIMIT]);
      refiners.push(['sort_date', 1]);
    }
    if (uri.startsWith('customers'))
      refiners.push([
        'include',
        CustomerPerfEndpointAllColumns.filter(
          (col) => !groupByDate || !['mutate_create_queue', 'mutate_update_queue'].includes(col),
        ).join(','),
      ]);

    return this.http
      .get(`${environment.app.resources.dashboard}${uri}?${this.utilitiesService.refinersToUrl(refiners)}`, {})
      .pipe(
        map((response: any) =>
          groupByDate
            ? this._transformSummaryData(response.data, lessThan7)
            : this._transformDataByType(response, dateDiff),
        ),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch keyfigs Performance', error, 'error');
          return throwError('Failed to fetch keyfigs Performance');
        }),
      );
  }

  private _transformSummaryData(data, lessThan7: boolean) {
    const summary: any = {
      [BUDGET_AMOUNT]: summationByMetric(data, BUDGET_AMOUNT),
      [COST]: summationByMetric(data, COST),
      [COST_DAY]: averageByMetric(data, COST),
      [COST_FOR_REVENUE]: summationByMetric(data, COST_FOR_REVENUE),
      [COST_FOR_CLICK]: summationByMetric(data, COST_FOR_CLICK),
      [COST_FOR_PROFIT]: summationByMetric(data, COST_FOR_PROFIT),
      [COST_FOR_VCPM]: summationByMetric(data, COST_FOR_VCPM),
      [IMPRESSIONS]: summationByMetric(data, IMPRESSIONS),
      [IMPRESSIONS_TOTAL]: summationByMetric(data, IMPRESSIONS_TOTAL),
      [CLICKS]: summationByMetric(data, CLICKS),
      [CONVERSIONS]: summationByMetric(data, CONVERSIONS),
      [REVENUE]: summationByMetric(data, REVENUE),
      [REVENUE_ADJUSTED]: summationByMetric(data, REVENUE_ADJUSTED),
      [REVENUE_TOTAL]: summationByMetric(data, REVENUE_TOTAL),
      [REVENUE_TOTAL_ROLLING_AVG]: summationByMetric(data, REVENUE_TOTAL_ROLLING_AVG),
      [REVENUE_ORGANIC]: summationByMetric(data, REVENUE_ORGANIC),
      [REVENUE_ORGANIC_ADJUSTED]: summationByMetric(data, REVENUE_ORGANIC_ADJUSTED),
      [REVENUE_ORGANIC_ROLLING_AVG]: summationByMetric(data, REVENUE_ORGANIC_ROLLING_AVG),
      [PROFIT_NET]: summationByMetric(data, PROFIT_NET),
      [PROFIT_NET_ADJUSTED]: summationByMetric(data, PROFIT_NET_ADJUSTED),
      [PROFIT_GROSS]: summationByMetric(data, PROFIT_GROSS),
      [PROFIT_GROSS_ADJUSTED]: summationByMetric(data, PROFIT_GROSS_ADJUSTED),
      [PRICE]: summationByMetric(data, PRICE),
      [CONVERSIONS_ADJUSTED]: summationByMetric(data, CONVERSIONS_ADJUSTED),

      [VIEW_IMPRESSIONS]: summationByMetric(data, VIEW_IMPRESSIONS),
      [CONVERSIONS_CLICKS]: summationByMetric(data, CONVERSIONS_CLICKS),
      [REVENUE_CLICKS]: summationByMetric(data, REVENUE_CLICKS),
    };

    const COST_WITHOUT_CLICKS = summary[COST] - summary[COST_FOR_CLICK];

    summary[CLICK_THROUGH_RATE] = getClickThroughRate(summary[CLICKS], summary[IMPRESSIONS_TOTAL]);
    summary[CLICK_THROUGH_RATE_CLICKS] = getClickThroughRate(summary[CLICKS], summary[IMPRESSIONS]);

    summary[CONVERSION_RATE] = getConversionRate(summary[CONVERSIONS], summary[CLICKS]);
    summary[CONVERSION_RATE_CLICKS] = getConversionRate(summary[CONVERSIONS_CLICKS], summary[CLICKS]);
    summary[CONVERSION_RATE_ADJUSTED] = getConversionRate(summary[CONVERSIONS_ADJUSTED], summary[CLICKS]);

    summary[COST_PER_CONVERSION] = getCostPerConversion(COST_WITHOUT_CLICKS, summary[CONVERSIONS]);
    summary[COST_PER_CONVERSION_CLICKS] = getCostPerConversion(COST_WITHOUT_CLICKS, summary[CONVERSIONS_CLICKS]);
    summary[COST_PER_CONVERSION_ADJUSTED] = getCostPerConversion(COST_WITHOUT_CLICKS, summary[CONVERSIONS_ADJUSTED]);

    summary[COST_REVENUE_RATIO] = getACoS(summary[COST_FOR_REVENUE], summary[REVENUE]);
    summary[COST_REVENUE_RATIO_CLICKS] = getACoS(summary[COST_FOR_REVENUE], summary[REVENUE_CLICKS]);
    summary[COST_REVENUE_RATIO_ADJUSTED] = getACoS(summary[COST_FOR_REVENUE], summary[REVENUE_ADJUSTED]);

    summary[ROAS] = getReturnOnAdSpend(summary[REVENUE], summary[COST_FOR_REVENUE]);
    summary[ROAS_CLICKS] = getReturnOnAdSpend(summary[REVENUE_CLICKS], summary[COST_FOR_REVENUE]);
    summary[ROAS_ADJUSTED] = getReturnOnAdSpend(summary[REVENUE_ADJUSTED], summary[COST_FOR_REVENUE]);

    summary[RETURN_ON_INVESTMENT] = getReturnOnInvestment(summary[PROFIT_GROSS], summary[COST_FOR_PROFIT]);
    summary[RETURN_ON_INVESTMENT_ADJUSTED] = getReturnOnInvestment(
      summary[PROFIT_GROSS_ADJUSTED],
      summary[COST_FOR_PROFIT],
    );

    summary[COST_PER_CLICK] = getEffectiveCPC(summary[COST], summary[CLICKS]);
    summary[COST_PER_MILLE] = getEffectiveCPM(summary[COST_FOR_VCPM], summary[VIEW_IMPRESSIONS]);

    if (summary[REVENUE] && summary[CONVERSIONS])
      summary[REVENUE_PER_CONVERSION] = summary[REVENUE] / summary[CONVERSIONS];

    if (!summary[REVENUE_TOTAL] && summary[REVENUE]) summary[REVENUE_TOTAL] = null;

    if (lessThan7) {
      summary[COST_REVENUE_TOTAL_RATIO] = data[data.length - 1]?.[COST_REVENUE_TOTAL_RATIO];
      summary[COST_REVENUE_TOTAL_RATIO_ROLLING_AVG] = data[data.length - 1]?.[COST_REVENUE_TOTAL_RATIO_ROLLING_AVG];
    } else {
      summary[COST_REVENUE_TOTAL_RATIO] = getACoS(summary[COST], summary[REVENUE_TOTAL]);
      summary[COST_REVENUE_TOTAL_RATIO_ROLLING_AVG] = getACoS(summary[COST], summary[REVENUE_TOTAL_ROLLING_AVG]);
    }

    summary.hasVcpm = !!data.find((d) => !!d.has_vcpm);
    if (!summary.hasVcpm && summary[VIEW_IMPRESSIONS] === 0) summary[VIEW_IMPRESSIONS] = null;

    return { points: convertMicrostoNumbers(deepClone(data)), summary };
  }

  private _transformDataByType({ meta, data }, days: number = 0): { total: number; items: any[] } {
    return {
      total: meta.pagination.total,
      items: convertMicrostoNumbers(data).map((item: any) => {
        const goals = [];
        if (!!item.performance_group_goals) {
          item.performance_group_goals.forEach((goal) => {
            goals.push({
              currency: goal.goal_currency,
              key: goal.goal_type,
              value: goal.goal_value / (currencyGoals.includes(goal.goal_type) ? 1e6 : 1),
            });
          });
        }
        const isRevenue = item.campaign_optimization_type === 'REVENUE';

        return {
          ...item,
          accountId: item.account_id,
          accountName: item.account_name,
          accountOptimize: item.account_optimize,
          accountTags: item.account_tags_user,
          platform: assertArray(item.account_platform || item.performance_group_platforms),
          adspertId: item.adspert_account_id || item.adspert_campaign_id?.split(':')[0],
          performanceGroupId: item.performance_group_id,
          performanceGroupName: item.performance_group_name,
          performanceGroupOptimizationType: item.performance_group_optimization_type,
          goals,
          portfolioId: item.adspert_portfolio_id,
          portfolioName: item.amz_portfolio_name,
          campaignId: item.adspert_campaign_id,
          campaignLabels: item.campaign_labels,
          campaignName: item.campaign_name,
          campaignOptimizationType: item.campaign_optimization_type,
          campaignAmzRasCampaign: item.campaign_amz_ras_campaign,
          campaignOptimize: item.campaign_optimize,
          campaignStatus: item.campaign_status,
          campaignType: item.campaign_type,
          adgroupId: item.adgroup_id,
          adspertAdgroupId: item.adspert_adgroup_id,
          adgroupName: item.adgroup_name,
          adgroupStatus: item.adgroup_status,
          criterionId: item.criterion_id,
          adspertCriterionId: item.adspert_criterion_id,
          criterionName: item.criterion_name,
          criterionMatchType: item.criterion_match_type,
          criterionNegative: item.criterion_negative,
          criterionStatus: item.criterion_status,
          criterionType: item.criterion_type,
          [PROFIT_GROSS]: isRevenue ? null : item[PROFIT_GROSS],
          [PROFIT_GROSS_ADJUSTED]: isRevenue ? null : item[PROFIT_GROSS_ADJUSTED],
          [PROFIT_NET]: isRevenue ? null : item[PROFIT_NET],
          [PROFIT_NET_ADJUSTED]: isRevenue ? null : item[PROFIT_NET_ADJUSTED],
          [COST_DAY]: item[COST] / days,
          [RETURN_ON_INVESTMENT]: isRevenue ? null : item[RETURN_ON_INVESTMENT],
          [RETURN_ON_INVESTMENT_ADJUSTED]: isRevenue ? null : item[RETURN_ON_INVESTMENT_ADJUSTED],
          mutateUpdateQueue: valueToCamelCase(item.mutate_update_queue) || [],
          mutateCreateQueue: valueToCamelCase(item.mutate_create_queue) || [],
        };
      }),
    };
  }
}
